import { AvatarGroup, AvatarGroupItem, AvatarGroupPopover, Button, Field, Label, Link, makeStyles, partitionAvatarGroupItems, Spinner, Switch, Text, Textarea, Tooltip } from "@fluentui/react-components";
import type { IGroupDetail, IdentityDetail, IConfigurationInfo, IUserDetail } from "../../../interfaces";
import { EConfigurationInfoTab } from "../../../interfaces";
import { useEffect, useState } from "react";
import { CheckmarkRegular, DismissRegular, EditRegular } from "@fluentui/react-icons";

export interface IOverviewTabProps {
    configurationInfo?: IConfigurationInfo;
    isLoading: boolean;
    userDetails: IUserDetail[];
    groupDetails: IGroupDetail[];
    onEnabledChange(isEnabled: boolean): void;
    onDescriptionChange(description: string): void;
    onTabChange(tab: EConfigurationInfoTab): void;
}

const useStyles = makeStyles({
    overViewItemContainer: {
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '20px',
        gap: '5px'
    },
    userContainer: {
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '20px',
        gap: '15px',
        alignItems: 'flex-start'
    },
    avatarGroups: {
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: '20px',
        gap: '15px',
    }
});

export const OverviewTab = (props: IOverviewTabProps) => {
    const { configurationInfo, isLoading, userDetails, groupDetails, onEnabledChange, onDescriptionChange, onTabChange } = props;
    const styles = useStyles();

    const [isSettingEnabled, setIsSettingEnabled] = useState(false);
    const [description, setDescription] = useState("");
    const [showEditDescriptionDialog, setShowEditDescriptionDialog] = useState(false);

    const hasUsers = configurationInfo?.userIds && configurationInfo.userIds.length > 0;
    const hasGroups = configurationInfo?.groupIds && configurationInfo.groupIds.length > 0;
    const hasUsersAndGroups = hasUsers && hasGroups;

    useEffect(() => {
        if (configurationInfo) {
            setIsSettingEnabled(configurationInfo.isEnabled);
            setDescription(configurationInfo.description ?? "");
        }
    }, [configurationInfo]);

    const onEnabledChanged = (isEnabled: boolean) => {
        setIsSettingEnabled(isEnabled);
        onEnabledChange(isEnabled);
    };

    const onDescriptionChanged = () => {
        setShowEditDescriptionDialog(false);
        onDescriptionChange(description ?? "");
    };

    const onCancelEditDescription = (): void => {
        setShowEditDescriptionDialog(false);
        setDescription(configurationInfo?.description ?? "");
    };

    const getAssignedUsersLabel = (): string => {
        if (hasUsers && hasGroups) {
            return "Specific set of users and groups";
        }

        if (hasUsers) {
            return "Specific set of users";
        }

        if (hasGroups) {
            return "Specific set of groups";
        }

        return "Entire organization";
    };

    const renderIdentityTooltip = (userDetail: IdentityDetail) => {
        return (<div>
            <div>{userDetail.displayName}</div>
            <div>{userDetail.email}</div>
        </div>);
    };

    const renderAvataGroups = (identities: IdentityDetail[]) => {
        const { inlineItems, overflowItems } = partitionAvatarGroupItems({
            items: identities,
        });

        return (
            <AvatarGroup>
                {inlineItems.map((identity, index) => (
                    <Tooltip key={`tooltip_${identity.id}_${index}`} content={renderIdentityTooltip(identity)} relationship={'label'}>
                        <AvatarGroupItem name={identity.displayName} key={identity.id} />
                    </Tooltip>
                ))}
                {overflowItems && (
                    <AvatarGroupPopover>
                        {overflowItems.map((identity, index) => (
                            <Tooltip key={`overflowTooltip_${identity.id}_${index}`} content={renderIdentityTooltip(identity)} relationship={'label'}>
                                <AvatarGroupItem name={identity.email} key={identity.id} />
                            </Tooltip>
                        ))}
                    </AvatarGroupPopover>
                )}
            </AvatarGroup>
        );
    };


    return (
        <div>
            <h3>Basic information</h3>
            <div className={styles.overViewItemContainer}>
                <div>
                    <Label weight="semibold">Description</Label>
                    {!showEditDescriptionDialog &&
                        <Tooltip content="Edit description" relationship={'label'}>
                            <Button appearance="transparent" icon={<EditRegular />} onClick={() => setShowEditDescriptionDialog(true)} />
                        </Tooltip>
                    }
                </div>
                {!showEditDescriptionDialog && <Text>{description}</Text>}
                {showEditDescriptionDialog &&
                    <>
                        <Textarea onChange={(_, data) => { setDescription(data.value); }} value={description} />
                        <div>
                            <Tooltip content="Accept description changes" relationship={'label'}>
                                <Button appearance="transparent" icon={<CheckmarkRegular />} onClick={() => onDescriptionChanged()} />
                            </Tooltip>
                            <Tooltip content="Disregard description changes" relationship={'label'}>
                                <Button appearance="transparent" icon={<DismissRegular />} onClick={() => onCancelEditDescription()} />
                            </Tooltip>
                        </div>
                    </>
                }
            </div>

            <div className={styles.overViewItemContainer}>
                <Label weight="semibold" >Deployment status</Label>
                <Field label={isSettingEnabled ? "Configuration is deployed" : "Configuration is not deployed"} orientation="horizontal">
                    <Switch
                        disabled={isLoading}
                        aria-label="Deployment status"
                        checked={isSettingEnabled}
                        onChange={(_, data) => onEnabledChanged(data.checked)}
                    />
                </Field>
            </div>

            <h3>Assigned users</h3>
            <div className={styles.userContainer}>
                <div>
                    {getAssignedUsersLabel()}
                </div>
                <div>
                    {isLoading && <Spinner size="small" label={"Loading..."} />}
                    {!isLoading && userDetails?.length > 0 &&
                        <div className={styles.avatarGroups}>
                            {hasUsersAndGroups && <Label>Assigned users</Label>}
                            {renderAvataGroups(userDetails)}
                        </div>
                    }
                    {!isLoading && groupDetails?.length > 0 &&
                        <div className={styles.avatarGroups}>
                              {hasUsersAndGroups && <Label>Assigned groups</Label>}
                            {renderAvataGroups(groupDetails)}
                        </div>
                    }
                </div>
                <Link onClick={() => onTabChange(EConfigurationInfoTab.Users)}>Edit users</Link>
            </div>
        </div>
    );
};
